import { useEffect } from "react";
import { useNav, isKnownRoute } from "./router";
import { routeReadAllowed } from "./routeTable";
import { SCREENS, MIGRATED_ROUTES } from "./screens";
import { can, useUser, landingRouteForRole } from "./access";
import { navProfileForUser } from "./navProfiles";
import { useRailCatalog } from "./chrome-rail";
import { useViewport } from "./useResponsive";
import AppLauncher from "./AppLauncher";
import { Btn, Icon, PageState, Skeleton, TagLabel } from "@v0y4ge/jrni-ui";
import { useNavComposition, getTenantContextState } from "../lib/tenant-vocab";
import { EventOverlay } from "../screens/events/detail/EventOverlay";
import { ErrorBoundary } from "./ErrorBoundary";
import { Screen as ScreenChrome } from "./chrome";
import { useT } from "../lib/i18n";
import { moduleKnownAbsent, useTenantModules } from "../lib/tenant-modules";
/* Routes that need a tenant module live in route-modules.ts. A direct visit on a tenant known
   not to have the module shows the not-available page; an unknown module list opens the screen as before. */
import { ROUTE_MODULE, landingForModules } from "./route-modules";

/**
 * AppRouter — renders the active route's screen, enforcing the read-level
 * capability guard (deep-link protection). Screens own their own chrome via the
 * ported <Screen> layout; this just resolves route → component.
 */
export function AppRouter() {
  const t = useT();
  const nav = useNav();
  const vp = useViewport();
  const tenantModules = useTenantModules();
  const user = useUser();
  /* Re-render when the served composition lands: navProfileForUser reads it synchronously, so without this the
     landing redirect never fires after a slow first read (CC 2026-10-19 walk 5 item 1). */
  useNavComposition();
  const contextSettling = getTenantContextState() === "loading";
  /* Phone & tablet: a tenant whose nav profile names its own landing goes there, inside its own shell and bottom
     bar (CC 2026-10-19 walk 4 item 5). The generic launcher stays only for a profile without one. */
  const profileLanding = navProfileForUser(user)?.landing;
  const mobileHome = profileLanding ? landingForModules(profileLanding, tenantModules) : null;
  if (nav.route === "dashboard" && vp !== "desktop") {
    /* Until the tenant context settles, a home-shaped skeleton, never the generic launcher it might replace. */
    if (contextSettling) return <HomeSkeleton cols={vp === "tablet" ? 3 : 2} />;
    return mobileHome && mobileHome !== "dashboard" ? <ReplaceTo route={mobileHome} /> : <AppLauncher />;
  }
  const migrated = MIGRATED_ROUTES.has(nav.route);
  const readable = routeReadAllowed(nav.route, can);
  // Every screen and the event drawer render inside their own boundary, so a
  // render crash is contained to that surface instead of unmounting the shell
  // (2026-07-27). `resetKey` clears a caught error as soon as the route changes,
  // which makes navigating away the natural recovery.
  /* The event drawer is an Events screen: not opened on a tenant known not to have Events. */
  const drawer = nav.drawerEvent && !moduleKnownAbsent("events", tenantModules)
    ? <ErrorBoundary scope={`drawer:${nav.drawerEvent}`} label={t("shell.appRouter.label", "Event drawer")} variant="pane" resetKey={String(nav.drawerEvent)}><EventOverlay ev={nav.drawerEvent} /></ErrorBoundary>
    : null;
  /* Inside the shell with a way home, never a bare dead end (CC 2026-10-19 walk 4 item 3). */
  if (!readable) return <><ScreenChrome><NoAccess /></ScreenChrome>{drawer}</>;
  const needsModule = ROUTE_MODULE[nav.route];
  if (needsModule && moduleKnownAbsent(needsModule, tenantModules)) return <><ScreenChrome><ModuleNotAvailable /></ScreenChrome>{drawer}</>;
  /* An address that names no route at all (a mistyped or stale link): say so,
     inside the normal chrome, at the address the user opened — never a silent
     detour to the dashboard (see router.tsx parseLoc). */
  if (!isKnownRoute(nav.route)) return <><ScreenChrome><UnknownAddress route={nav.route} /></ScreenChrome>{drawer}</>;
  const Screen = SCREENS[nav.route];
  if (!Screen) return <><UnmigratedBadge /><NotFound route={nav.route} />{drawer}</>;
  return (
    <>
      {!migrated ? <UnmigratedBadge /> : null}
      <ErrorBoundary scope={nav.route} label={nav.route} resetKey={nav.route}
        wrap={(fallback) => <ScreenChrome>{fallback}</ScreenChrome>}><Screen /></ErrorBoundary>
      {drawer}
    </>
  );
}

/**
 * Unmigrated badge — fixed top-right, very high z-index, non-interactive.
 * Shows on every route NOT in MIGRATED_ROUTES (not yet wired to the live data
 * layer). A screen graduates — and the badge disappears — the moment it's added
 * to REAL_SCREENS. Built on the design-system TagLabel (token-based, no hex).
 */
function UnmigratedBadge() {
  const t = useT();
  return (
    <div aria-label={t("shell.appRouter.thisScreenIsNotYetMigratedToTheLiveDataLayer", "This screen is not yet migrated to the live data layer")}
      style={{ position: "fixed", top: 12, right: 12, zIndex: 2000000, pointerEvents: "none", borderRadius: 999, boxShadow: "0 4px 14px rgba(20, 16, 46, 0.18)" }}>
      <TagLabel color="orange" type="dot">{t("shell.appRouter.unmigrated", "Unmigrated")}</TagLabel>
    </div>
  );
}

/** Tile-shaped placeholders for the phone/tablet home while the tenant context is still loading. */
function HomeSkeleton({ cols }: { cols: number }) {
  const t = useT();
  return (
    <div role="status" aria-busy="true" aria-label={t("common.status.loading", "Loading…")} data-testid="home-skeleton"
      style={{ padding: "20px 16px", display: "flex", flexDirection: "column", gap: 16 }}>
      <Skeleton variant="text" width="45%" height={22} />
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: 12 }}>
        {Array.from({ length: cols * 2 }, (_, i) => <Skeleton key={i} height={132} />)}
      </div>
      <Skeleton variant="text" width="25%" />
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: 12 }}>
        {Array.from({ length: cols }, (_, i) => <Skeleton key={i} height={104} />)}
      </div>
    </div>
  );
}
/** Swaps the address for the tenant's own landing (replace, so Back does not return to the launcher). */
function ReplaceTo({ route }: { route: string }) {
  const nav = useNav();
  useEffect(() => { nav.replace(route); }, [nav, route]);
  return null;
}
function Centered({ title, body }: { title: string; body: string }) {
  return (
    <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 40, fontFamily: "var(--jrni-font-family-sans)" }}>
      <div style={{ maxWidth: 420, textAlign: "center" }}>
        <div style={{ fontSize: 19, fontWeight: 700, color: "var(--jrni-color-neutral-1)", marginBottom: 8 }}>{title}</div>
        <div style={{ fontSize: 14, color: "var(--jrni-color-neutral-2)", lineHeight: 1.5 }}>{body}</div>
      </div>
    </div>
  );
}
function NoAccess() {
  const t = useT();
  const nav = useNav();
  const u = useUser();
  const rail = useRailCatalog();
  const tenantModules = useTenantModules();
  const home = landingForModules(navProfileForUser(u)?.landing || landingRouteForRole(u.role), tenantModules);
  const homeLabel = rail.find((i) => i.route === home)?.label;
  return (
    <div style={{ padding: 24, display: "flex", justifyContent: "center" }}>
      <PageState empty testid="no-access" style={{ maxWidth: 560, width: "100%" }}
        icon={<Icon.lock size={20} />}
        emptyTitle={t("shell.appRouter.youDonTHaveAccessToThis", "You don't have access to this")}
        emptyBody={t("shell.appRouter.yourRoleCanTOpenThisSurfaceAskAnAdminIfYou", "Your role can't open this surface. Ask an admin if you need access.")}
        action={home && home !== nav.route ? <Btn onClick={() => nav.replace(home)}>{homeLabel ? t("shell.appRouter.goToNamed.icu", "Go to {name}", { name: homeLabel }) : t("shell.appRouter.goHome", "Go to your home page")}</Btn> : null} />
    </div>
  );
}
/* Suggested keys, plain t() with fallbacks: shell.appRouter.moduleNotAvailableTitle, shell.appRouter.moduleNotAvailableBody. */
function ModuleNotAvailable() {
  const t = useT(); return <Centered title={t("shell.appRouter.moduleNotAvailableTitle", "This isn't available in your workspace")} body={t("shell.appRouter.moduleNotAvailableBody", "Your workspace doesn't include this module. Ask an admin if you need it.")} />; }
/** The nearest route above an unknown address that this role may open —
 *  /workforce/myschedule/2026-12-01 → workforce.myschedule. Null when none. */
function readableParent(route: string): string | null {
  const parts = route.split(".");
  for (let n = parts.length - 1; n > 0; n--) {
    const r = parts.slice(0, n).join(".");
    if (!isKnownRoute(r)) continue;
    if (routeReadAllowed(r, can)) return r;
  }
  return null;
}
function UnknownAddress({ route }: { route: string }) {
  const t = useT();
  const nav = useNav();
  const u = useUser();
  const rail = useRailCatalog();
  const tenantModules = useTenantModules();
  const home = landingForModules(navProfileForUser(u)?.landing || landingRouteForRole(u.role), tenantModules);
  const parent = readableParent(route);
  const parentLabel = parent ? rail.find((i) => i.route === parent)?.label : undefined;
  const homeLabel = rail.find((i) => i.route === home)?.label;
  const path = typeof window !== "undefined" ? window.location.pathname : "/" + route.split(".").join("/");
  return (
    <div style={{ padding: 24, display: "flex", justifyContent: "center" }}>
      <PageState empty testid="unknown-address" style={{ maxWidth: 560, width: "100%" }}
        icon={<Icon.search size={20} />}
        emptyTitle={t("shell.appRouter.unknownAddressTitle", "This page doesn't exist")}
        emptyBody={t("shell.appRouter.unknownAddressBody.icu", "Nothing in Studio lives at {path}. The link may be mistyped or out of date.", { path })}
        action={parent && parent !== home
          ? <Btn onClick={() => nav.replace(parent)}>{parentLabel ? t("shell.appRouter.openNamed.icu", "Open {name}", { name: parentLabel }) : t("shell.appRouter.openParent", "Open the page above")}</Btn>
          : <Btn onClick={() => nav.replace(home)}>{homeLabel ? t("shell.appRouter.goToNamed.icu", "Go to {name}", { name: homeLabel }) : t("shell.appRouter.goHome", "Go to your home page")}</Btn>}
        secondaryAction={parent && parent !== home
          ? <Btn variant="secondary" onClick={() => nav.replace(home)}>{homeLabel ? t("shell.appRouter.goToNamed.icu", "Go to {name}", { name: homeLabel }) : t("shell.appRouter.goHome", "Go to your home page")}</Btn>
          : undefined} />
    </div>
  );
}
function NotFound({ route }: { route: string }) {
  const t = useT(); return <Centered title={t("shell.appRouter.pageNotFound", "Page not found")} body={t("shell.appRouter.noScreenIsRegisteredForRoute.icu", "No screen is registered for \"{route}\".", { route })} />; }
